/**
 * Optimized D3 rendering utilities
 * 
 * This is a refactored and simplified version of d3Optimizer.ts that contains
 * only the actively used optimization functions.
 */
import * as d3 from 'd3';
import { useRef, useEffect, useState, useMemo } from 'react';

/**
 * Checks if an element is within the current viewport
 * @param element Element with position data
 * @param viewportBounds Current viewport boundaries
 * @param buffer Additional buffer zone around viewport
 * @returns boolean indicating if element is visible
 */
export const isElementInViewport = (
  element: { x: number; y: number; width?: number; height?: number },
  viewportBounds: { x: number; y: number; width: number; height: number },
  buffer: number = 50
): boolean => {
  // Add default width/height if not provided
  const w = element.width || 0;
  const h = element.height || 0;
  
  // Add buffer to viewport
  const expandedBounds = {
    x: viewportBounds.x - buffer,
    y: viewportBounds.y - buffer,
    width: viewportBounds.width + buffer * 2,
    height: viewportBounds.height + buffer * 2
  };
  
  // Check if element intersects with expanded viewport
  return !(
    element.x + w < expandedBounds.x ||
    element.y + h < expandedBounds.y ||
    element.x > expandedBounds.x + expandedBounds.width ||
    element.y > expandedBounds.y + expandedBounds.height
  );
};

/**
 * Hook to optimize D3 zoom behavior
 * @param svgRef Reference to SVG element
 * @param options Zoom configuration options
 * @returns Zoom state and control functions
 */
export const useOptimizedZoom = (
  svgRef: React.RefObject<SVGSVGElement>,
  options?: {
    initialTransform?: d3.ZoomTransform;
    minZoom?: number;
    maxZoom?: number;
    onZoom?: (transform: d3.ZoomTransform) => void;
    throttleTime?: number;
  }
) => {
  const defaultOptions = {
    minZoom: 0.5,
    maxZoom: 10,
    throttleTime: 20, // ms
    ...options
  };
  
  const [isZooming, setIsZooming] = useState(false);
  const [zoomTransform, setZoomTransform] = useState<d3.ZoomTransform>(
    options?.initialTransform || d3.zoomIdentity
  );
  
  // Throttling mechanism
  const lastUpdateRef = useRef<number>(0);
  const throttledUpdateRef = useRef<number | null>(null);
  
  // Initialize zoom behavior
  useEffect(() => {
    if (!svgRef.current) return;
    
    // Capture the current SVG ref value at the start of the effect
    const currentSvg = svgRef.current;
    
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([defaultOptions.minZoom, defaultOptions.maxZoom])
      .on('start', () => {
        setIsZooming(true);
      })
      .on('zoom', (event: d3.D3ZoomEvent<SVGSVGElement, unknown>) => {
        const now = Date.now();
        
        // Throttle updates during rapid zooming
        if (now - lastUpdateRef.current < defaultOptions.throttleTime) {
          // Cancel any pending update
          if (throttledUpdateRef.current !== null) {
            cancelAnimationFrame(throttledUpdateRef.current);
          }
          
          // Schedule a new update
          throttledUpdateRef.current = requestAnimationFrame(() => {
            setZoomTransform(event.transform);
            if (defaultOptions.onZoom) {
              defaultOptions.onZoom(event.transform);
            }
            lastUpdateRef.current = now;
            throttledUpdateRef.current = null;
          });
        } else {
          // Update immediately if enough time has passed
          setZoomTransform(event.transform);
          if (defaultOptions.onZoom) {
            defaultOptions.onZoom(event.transform);
          }
          lastUpdateRef.current = now;
        }
      })
      .on('end', () => {
        setIsZooming(false);
        
        // Make sure we capture the final state
        if (throttledUpdateRef.current !== null) {
          cancelAnimationFrame(throttledUpdateRef.current);
          throttledUpdateRef.current = null;
        }
      });
    
    d3.select(currentSvg).call(zoom);
    
    // Apply initial transform if provided
    if (options?.initialTransform) {
      d3.select(currentSvg).call(zoom.transform, options.initialTransform);
    }
    
    return () => {
      // Cleanup
      if (throttledUpdateRef.current !== null) {
        cancelAnimationFrame(throttledUpdateRef.current);
      }
      d3.select(currentSvg).on('.zoom', null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svgRef.current, defaultOptions.minZoom, defaultOptions.maxZoom]);
  
  // Zoom control functions
  const zoomTo = (newZoom: number, x?: number, y?: number) => {
    if (!svgRef.current || !d3.zoomTransform) return;
    
    const svg = d3.select(svgRef.current);
    const width = svg.node()?.getBoundingClientRect().width || 0;
    const height = svg.node()?.getBoundingClientRect().height || 0;
    
    // Default to center if coordinates not provided
    const centerX = x !== undefined ? x : width / 2;
    const centerY = y !== undefined ? y : height / 2;
    
    // Zoom to the specified level at the given point
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([defaultOptions.minZoom, defaultOptions.maxZoom]);
    
    const transform = d3.zoomIdentity
      .translate(centerX, centerY)
      .scale(newZoom)
      .translate(-centerX, -centerY);
    
    svg.transition()
      .duration(250)
      .call(zoom.transform as any, transform);
  };
  
  return {
    isZooming,
    zoomTransform,
    zoomTo
  };
};

/**
 * Hook to only render D3 elements within the current viewport
 * @param allElements All possible elements to render
 * @param viewportBounds Current viewport boundaries
 * @param zoomLevel Current zoom level
 * @returns Elements that should be rendered
 */
export const useViewportAwareRendering = <T extends { x: number; y: number; width?: number; height?: number }>(
  allElements: T[],
  viewportBounds: { x: number; y: number; width: number; height: number },
  zoomLevel: number
) => {
  // Buffer size increases at lower zoom levels to ensure smooth scrolling
  const bufferFactor = Math.max(1, 2 / zoomLevel);
  const bufferSize = 100 * bufferFactor;
  
  return useMemo(() => {
    // Filter elements to only those in viewport + buffer zone
    return allElements.filter(element => 
      isElementInViewport(element, viewportBounds, bufferSize)
    );
  }, [allElements, viewportBounds, bufferSize]);
};

/**
 * Utility to debounce expensive operations during interaction
 * @param isInteracting Flag indicating if user is currently interacting
 * @param fullQualityFn Function to run for full quality rendering
 * @param lowQualityFn Function to run for low quality rendering during interaction
 */
export const useDynamicQuality = (
  isInteracting: boolean,
  fullQualityFn: () => void,
  lowQualityFn: () => void
) => {
  useEffect(() => {
    if (isInteracting) {
      // Use low quality during interaction
      lowQualityFn();
    } else {
      // Use full quality when not interacting
      const timeoutId = setTimeout(() => {
        fullQualityFn();
      }, 200); // Small delay to ensure interaction has completely stopped
      
      return () => clearTimeout(timeoutId);
    }
  }, [isInteracting, fullQualityFn, lowQualityFn]);
};

/**
 * Utility to batch multiple D3 operations for better performance
 * @param operations Array of functions to execute
 */
export const batchD3Operations = (operations: Array<() => void>) => {
  return () => {
    // Use requestAnimationFrame to batch operations
    requestAnimationFrame(() => {
      operations.forEach(operation => operation());
    });
  };
};

/**
 * Creates a stable callback that maintains the same reference
 * even when its dependencies change. Useful for event handlers
 * in D3 selections.
 * 
 * @param callback The original callback function
 * @returns A stable callback that will always have the same reference
 */
export function useStableCallback<T extends (...args: any[]) => any>(callback: T): T {
  const callbackRef = useRef(callback);
  
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);
  
  return useMemo(
    () => ((...args: any[]) => callbackRef.current(...args)) as T,
    []
  );
}

/**
 * Hook to track component render performance in development
 * @param componentName Name of the component for logging
 * @param enabled Whether tracking is enabled (defaults to process.env.NODE_ENV === 'development')
 * @returns Object with renderCount
 */
export const useRenderPerformance = (
  componentName: string, 
  enabled: boolean = process.env.NODE_ENV === 'development'
) => {
  const renderCount = useRef(0);
  const renderTimeRef = useRef(Date.now());
  
  useEffect(() => {
    renderCount.current += 1;
    const now = Date.now();
    renderTimeRef.current = now;
    
    if (enabled) {
    }
    
    return () => {
      if (enabled) {
      }
    };
  });
  
  return { renderCount: renderCount.current };
};

/**
 * Hook to prevent duplicate renders and track render reasons
 * More advanced version of useRenderPerformance that also helps debug re-renders
 * 
 * @param componentName Name for tracking
 * @param dependencies Array of dependencies to check
 * @param depNames Optional array of dependency names for better logging
 * @returns Object with renderCount and lastRenderReason
 */
export const usePreventDuplicateRenders = (
  componentName: string,
  dependencies: any[],
  depNames?: string[]
) => {
  const renderCount = useRef(0);
  const previousDepsRef = useRef<any[]>([]);
  const lastRenderReasonRef = useRef<string>('initial');
  
  // Check what caused this render
  useEffect(() => {
    const prevDeps = previousDepsRef.current;
    
    // On first render, just store deps
    if (prevDeps.length === 0) {
      previousDepsRef.current = [...dependencies];
      return;
    }
    
    // Find which dependency changed
    const changedDeps = dependencies.reduce((result, dep, index) => {
      if (dep !== prevDeps[index]) {
        result.push(index);
      }
      return result;
    }, [] as number[]);
    
    // Update reason and log
    if (changedDeps.length > 0) {
      let reason: string;
      
      if (depNames && depNames.length === dependencies.length) {
        // Use provided names for the dependencies
        reason = `deps changed: ${changedDeps.map((i: number) => depNames[i]).join(', ')}`;
      } else {
        // Just use indices
        reason = `deps changed: indices ${changedDeps.join(', ')}`;
      }
      
      lastRenderReasonRef.current = reason;
      
      if (process.env.NODE_ENV === 'development') {
      }
    }
    
    // Update previous deps - create a new array to avoid reference issues
    previousDepsRef.current = [...dependencies];
  });
  
  // Count renders
  useEffect(() => {
    renderCount.current += 1;
    
    if (process.env.NODE_ENV === 'development') {
    }
    
    return () => {
      if (process.env.NODE_ENV === 'development') {
      }
    };
  }, [componentName]);
  
  return {
    renderCount: renderCount.current,
    lastRenderReason: lastRenderReasonRef.current
  };
};

/**
 * Create optimized preset handlers that don't cause unnecessary rerenders
 */
export const useOptimizedPresets = (
  presets: any[] | undefined,
  activePresetId: string | undefined,
  applyPreset: (presetId: string) => void
) => {
  // Memoize the preset application function
  const optimizedApplyPreset = useStableCallback(applyPreset);
  
  // Return optimized handlers
  return useMemo(() => ({
    applyPreset: optimizedApplyPreset,
    presets,
    activePresetId
  }), [optimizedApplyPreset, presets, activePresetId]);
};

